/*
 * Browser speech for the widget: voice input (Web Speech recognition) and read-aloud (speechSynthesis).
 * Both depend on the visitor's browser, so callers hide the controls when support is missing.
 */

export const SpeechRecognitionApi = typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition || null
    : null;

export const canSpeak = typeof window !== 'undefined'
    && 'speechSynthesis' in window
    && typeof window.SpeechSynthesisUtterance === 'function';

// The most natural English voice each browser ships, best first: Edge's neural Emma, other Edge
// neural voices, Chrome's Google voice, then Safari's Samantha.
const PREFERRED_ENGLISH_VOICES = [
    /Microsoft Emma.*(Natural|Online)/i,
    /Microsoft .*(Natural|Online).*English/i,
    /Google US English/i,
    /Samantha/i,
];

// Chrome stops long utterances after ~15 seconds, so answers are queued in sentence-sized chunks.
const MAX_CHUNK_LENGTH = 220;

// getVoices() is empty until the browser has loaded its voice list; asking early starts the load.
if (canSpeak) window.speechSynthesis.getVoices();

const pickVoice = ({ localOnly = false } = {}) => {
    const voices = window.speechSynthesis.getVoices().filter((v) => !localOnly || v.localService);
    if (!voices.length) return null;
    const lang = (navigator.language || 'en-US').toLowerCase();
    if (lang.startsWith('en')) {
        for (const pattern of PREFERRED_ENGLISH_VOICES) {
            const voice = voices.find((v) => pattern.test(v.name));
            if (voice) return voice;
        }
    }
    return voices.find((v) => v.lang.toLowerCase() === lang)
        || voices.find((v) => v.lang.toLowerCase().startsWith(lang.slice(0, 2)))
        || voices.find((v) => v.default)
        || voices[0];
};

// Markdown answers read as plain sentences: no symbols, code, images or raw URLs.
const toSpeakableText = (markdown) => String(markdown || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/^\s*[-+]\s+/gm, '')
    .replace(/-{3,}|[`*_#>|~]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const splitIntoChunks = (text) => {
    const chunks = [];
    let current = '';
    for (const sentence of text.match(/[^.!?]+[.!?]*/g) || [text]) {
        if (current && (current + sentence).length > MAX_CHUNK_LENGTH) {
            chunks.push(current);
            current = '';
        }
        current += sentence;
    }
    if (current.trim()) chunks.push(current);
    return chunks;
};

// Chrome can garbage-collect queued utterances (their events then never fire), so they are kept here.
let activeUtterances = [];

const queue = (chunks, voice, { onEnd, onVoiceError }) => {
    const synth = window.speechSynthesis;
    let settled = false;
    const settle = (callback) => {
        if (settled) return;
        settled = true;
        callback();
    };
    activeUtterances = chunks.map((chunk, index) => {
        const utterance = new window.SpeechSynthesisUtterance(chunk.trim());
        if (voice) {
            utterance.voice = voice;
            utterance.lang = voice.lang;
        }
        utterance.onerror = (event) => {
            // Stopped by the user or replaced by another answer: not a failure.
            if (event.error === 'interrupted' || event.error === 'canceled') settle(onEnd);
            else settle(() => onVoiceError(event.error));
        };
        if (index === chunks.length - 1) utterance.onend = () => settle(onEnd);
        return utterance;
    });
    synth.resume(); // Chrome sometimes leaves the queue paused after a cancel.
    activeUtterances.forEach((utterance) => synth.speak(utterance));
};

// Reads an answer aloud, replacing anything already playing. onEnd runs when it finishes or is stopped;
// onError(code) when the browser can't speak it at all. An online voice (Edge's Emma Natural, Google)
// that fails is retried once with a voice installed on the device.
export function speak(markdown, onEnd, onError) {
    const synth = window.speechSynthesis;
    if (synth.speaking || synth.pending) synth.cancel();
    const chunks = splitIntoChunks(toSpeakableText(markdown));
    if (!chunks.length) {
        onEnd();
        return;
    }
    const voice = pickVoice();
    queue(chunks, voice, {
        onEnd,
        onVoiceError: (code) => {
            const localVoice = voice && !voice.localService ? pickVoice({ localOnly: true }) : null;
            if (!localVoice) {
                console.warn('[Aegis Assist] read aloud failed:', code);
                onError(code);
                return;
            }
            console.warn(`[Aegis Assist] voice "${voice.name}" failed (${code}); retrying with "${localVoice.name}"`);
            synth.cancel();
            queue(chunks, localVoice, {
                onEnd,
                onVoiceError: (retryCode) => {
                    console.warn('[Aegis Assist] read aloud failed:', retryCode);
                    onError(retryCode);
                },
            });
        },
    });
}

export function stopSpeaking() {
    if (!canSpeak) return;
    window.speechSynthesis.cancel();
    activeUtterances = [];
}

// What the visitor sees when voice input fails, by Web Speech error code.
const RECOGNITION_ERRORS = {
    'not-allowed': 'Microphone access is blocked. Allow it from the icon in the address bar, then try again.',
    'service-not-allowed': 'Microphone access is blocked. Allow it from the icon in the address bar, then try again.',
    'audio-capture': 'No microphone was found. Connect one and try again.',
    network: "Voice input couldn't reach the browser's speech service. Check your connection and try again.",
    'no-speech': "Didn't catch that. Tap the mic and try speaking again.",
    'language-not-supported': "Voice input doesn't support this browser's language.",
};

export const recognitionErrorMessage = (code) => RECOGNITION_ERRORS[code] || "Voice input isn't available right now.";
